import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Star, ArrowLeft, Plus, Share2, Globe, Lock, Trash2, Edit2,
  User, LogOut, Settings, Menu, X, Copy, Check, MapPin, Search
} from "lucide-react";

const CollectionDetail = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Add restaurant dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadCollection = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/collections/${collectionId}`, { withCredentials: true });
      setCollection(response.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 404) {
        toast.error("Collection nicht gefunden oder kein Zugriff");
        navigate('/collections');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  const searchRestaurants = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/restaurants`, {
        params: { search: query, limit: 10 },
        withCredentials: true
      });
      // Filter out restaurants already in collection
      const existingIds = collection?.restaurants?.map(r => r.restaurant_id) || [];
      setSearchResults((response.data || []).filter(r => !existingIds.includes(r.restaurant_id)));
    } catch (error) {
      console.warn("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const addRestaurant = async (restaurant) => {
    try {
      await axios.post(
        `${API}/collections/${collectionId}/restaurants/${restaurant.restaurant_id}`,
        {},
        { withCredentials: true }
      );
      
      // Update local state
      setCollection(prev => ({
        ...prev,
        restaurants: [{ ...restaurant, added_at: new Date().toISOString() }, ...(prev.restaurants || [])],
        restaurant_count: (prev.restaurant_count || 0) + 1
      }));
      
      // Remove from search results
      setSearchResults(prev => prev.filter(r => r.restaurant_id !== restaurant.restaurant_id));
      
      toast.success(`${restaurant.name} hinzugefügt!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const removeRestaurant = async (restaurantId) => {
    try {
      await axios.delete(
        `${API}/collections/${collectionId}/restaurants/${restaurantId}`,
        { withCredentials: true }
      );
      
      setCollection(prev => ({
        ...prev,
        restaurants: prev.restaurants.filter(r => r.restaurant_id !== restaurantId),
        restaurant_count: Math.max(0, (prev.restaurant_count || 0) - 1)
      }));
      
      toast.success("Restaurant entfernt");
    } catch (error) {
      toast.error("Fehler beim Entfernen");
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/collections/shared/${collection.share_code}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link kopiert!");
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Collection nicht gefunden</p>
      </div>
    );
  }

  const isOwner = collection.is_owner;

  return (
    <div className="min-h-screen bg-background" data-testid="collection-detail-page">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/collections')}
                className="mr-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <Star className="w-5 h-5 text-primary fill-primary" />
                <span className="font-semibold gradient-text hidden sm:inline">Étoile</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hidden md:flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />{t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />{t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
          <div className="flex items-start gap-4">
            <span className="text-5xl">{collection.emoji}</span>
            <div>
              <h1 className="text-3xl font-bold tracking-tight mb-1">{collection.name}</h1>
              {collection.description && (
                <p className="text-muted-foreground mb-2">{collection.description}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {collection.is_public ? (
                  <Badge variant="outline">
                    <Globe className="w-3 h-3 mr-1" />
                    Öffentlich
                  </Badge>
                ) : (
                  <Badge variant="outline">
                    <Lock className="w-3 h-3 mr-1" />
                    Privat
                  </Badge>
                )}
                <span>{collection.restaurant_count} Restaurants</span>
                {collection.owner && !isOwner && (
                  <span>• von {collection.owner.name}</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyShareLink}>
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              Teilen
            </Button>
            {isOwner && (
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="bg-primary text-primary-foreground"
                data-testid="add-restaurant-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Restaurant hinzufügen
              </Button>
            )}
          </div>
        </div>

        {/* Restaurants List */}
        {collection.restaurants?.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">Noch keine Restaurants in dieser Collection</p>
              {isOwner && (
                <Button onClick={() => setShowAddDialog(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Erstes Restaurant hinzufügen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {collection.restaurants?.map((restaurant) => (
              <Card 
                key={restaurant.restaurant_id}
                className="bg-card border-border hover:border-primary/30 transition-all"
                data-testid={`collection-restaurant-${restaurant.restaurant_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-4 cursor-pointer flex-1"
                      onClick={() => navigate('/restaurants')}
                    >
                      <div className="flex gap-0.5">
                        {[...Array(restaurant.stars || 1)].map((_, i) => (
                          <Star key={`coll-star-${restaurant.restaurant_id}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                        ))}
                      </div>
                      <div>
                        <h3 className="font-semibold hover:text-primary transition-colors">
                          {restaurant.name}
                        </h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {restaurant.city}, {restaurant.country}
                        </p>
                      </div>
                    </div>
                    
                    {isOwner && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRestaurant(restaurant.restaurant_id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add Restaurant Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Restaurant hinzufügen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Restaurant suchen..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchRestaurants(e.target.value);
                }}
                className="pl-10 bg-transparent border-border"
                data-testid="search-restaurant-input"
              />
            </div>
            
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {searching && (
                <div className="text-center py-4">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              )}
              
              {!searching && searchResults.length === 0 && searchQuery && (
                <p className="text-center text-muted-foreground py-4">Keine Restaurants gefunden</p>
              )}
              
              {searchResults.map((restaurant) => (
                <div
                  key={restaurant.restaurant_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex gap-0.5">
                      {[...Array(restaurant.stars || 1)].map((_, i) => (
                        <Star key={`search-star-${restaurant.restaurant_id}-${i}`} className="w-3 h-3 text-primary fill-primary" />
                      ))}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{restaurant.name}</p>
                      <p className="text-xs text-muted-foreground">{restaurant.city}, {restaurant.country}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => addRestaurant(restaurant)}
                    className="bg-primary text-primary-foreground h-8"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionDetail;
